import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const LoadingScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [phase, setPhase] = useState(0); // 0: logo, 1: text, 2: exit
  const { t } = useTranslation();

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 400),
      setTimeout(() => setPhase(2), 1200),
      setTimeout(() => {
        setIsVisible(false);
        if (onComplete) onComplete();
      }, 1600)
    ];

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-[var(--color-bg)]"
        data-testid="loading-screen"
        initial={{ opacity: 1 }}
        animate={{ opacity: phase === 2 ? 0 : 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex flex-col items-center">
          {/* Cerchi concentrici animati */}
          <div className="relative w-28 h-28 flex items-center justify-center">
            {/* Cerchio esterno - pulse lento */}
            <motion.div
              className="absolute w-28 h-28 rounded-full border-2 border-[var(--color-primary)]/20"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1.2, opacity: [0, 0.5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
            />
            
            {/* Cerchio medio - gradient */}
            <motion.div
              className="absolute w-20 h-20 rounded-full"
              style={{
                background: 'linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-accent-light) 100%)'
              }}
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
            />
            
            {/* Logo centrale */}
            <motion.div
              className="relative z-10 w-16 h-16 rounded-2xl bg-[var(--color-primary)] flex items-center justify-center shadow-lg"
              style={{ boxShadow: '0 8px 32px rgba(13, 148, 136, 0.3)' }}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 0.1,
                ease: [0.34, 1.56, 0.64, 1] // spring-like
              }}
            >
              <motion.span 
                className="text-3xl font-extrabold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
              >
                F
              </motion.span>
            </motion.div>
          </div>

          {/* Brand name con reveal */}
          <motion.div 
            className="mt-8 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
          >
            <motion.div
              className="flex items-center gap-0.5"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              initial={{ y: 30 }}
              animate={{ y: phase >= 1 ? 0 : 30 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <span className="text-2xl font-extrabold tracking-tight text-[var(--color-text)]">
                Faccio
              </span>
              <span className="text-2xl font-extrabold tracking-tight text-[var(--color-primary)]">
                App
              </span>
            </motion.div>
          </motion.div>

          {/* Tagline sottile */}
          <motion.p
            className="mt-3 text-sm text-[var(--color-text-muted)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 1 ? 0.7 : 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {t('loading.tagline')}
          </motion.p>

          {/* Progress dots invece della barra */}
          <motion.div 
            className="mt-8 flex gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ 
                  backgroundColor: 'var(--color-primary)'
                }}
                initial={{ scale: 0 }}
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 1, 0.3]
                }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.15,
                  repeat: Infinity,
                  repeatDelay: 0.3
                }}
              />
            ))}
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoadingScreen;
